<template>
  <slot
    name="trigger"
    v-bind="{ open }"
  />
  <Teleport to="body">
    <Transition name="bottom-sheet">
      <div
        v-if="isOpen"
        class="bottom-sheet-mask"
        v-bind="attrs"
        @click.self="close"
      >
        <div
          class="bottom-sheet-mask__container"
          :class="{
            'bottom-sheet-mask__container--dragging': isDraggingDown,
          }"
          :style="sheetStyle"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @touchcancel="handleTouchCancel"
        >
          <div class="bottom-sheet-mask__container--header">
            <button
              class="bottom-sheet-mask__container--header--close-button"
              type="button"
              data-qa="bottom-sheet-close-button"
              aria-label="Close"
              @click.stop="close"
            >
              <CloseIcon aria-hidden="true" />
            </button>

            <p class="bottom-sheet-mask__container--header--title">
              {{ title }}
            </p>

            <div
              aria-hidden="true"
              class="bottom-sheet-mask__container--header--spacer"
            />
          </div>

          <div
            ref="bodyRef"
            class="bottom-sheet-mask__container--body"
            :class="{
              'bottom-sheet-mask__container--body--has-footer': hasFooterSlot,
            }"
            @scroll.passive="updateStickyFooterShadow"
          >
            <slot />
          </div>

          <div
            v-if="hasFooterSlot"
            class="bottom-sheet-mask__container--footer"
            :class="{
              'bottom-sheet-mask__container--footer-show-shadow':
                hasFooterSlot && showStickyFooterShadow,
            }"
          >
            <slot
              name="footer"
              v-bind="{ close }"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  useAttrs,
  useSlots,
} from 'vue'
import CloseIcon from '@shared/assets/icon/CloseIcon.vue'
import Tracker from '@shared/Tracker.js'

const emit = defineEmits(['open', 'close'])

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  screenName: {
    type: String,
    default: '',
  },
})

const attrs = useAttrs()
const slots = useSlots()
const hasFooterSlot = computed(() => !!slots.footer)

const isOpen = ref(false)
const dragOffsetY = ref(0)
const previousBodyOverflow = ref('')
const showStickyFooterShadow = ref(false)

const bodyRef = ref(null)

const touchStartY = ref(0)
const touchLastY = ref(0)
const isDraggingDown = ref(false)
const startedFromBody = ref(false)

const SWIPE_CLOSE_THRESHOLD = 96

const sheetStyle = computed(() => ({
  '--bottom-sheet-drag-offset': `${Math.max(dragOffsetY.value, 0)}px`,
}))

const handleKeydown = (event) => {
  if (event.key !== 'Escape') return
  close()
}

const disableDocumentScroll = () => {
  previousBodyOverflow.value = document.body.style.overflow
  document.body.style.overflow = 'hidden'
}

const restoreDocumentScroll = () => {
  document.body.style.overflow = previousBodyOverflow.value
}

const updateStickyFooterShadow = () => {
  if (!hasFooterSlot.value || !bodyRef.value) {
    showStickyFooterShadow.value = false
    return
  }

  const remainingScrollSpace =
    bodyRef.value.scrollHeight -
    bodyRef.value.scrollTop -
    bodyRef.value.clientHeight

  showStickyFooterShadow.value = remainingScrollSpace > 1
}

const open = async () => {
  if (isOpen.value) return

  dragOffsetY.value = 0
  isDraggingDown.value = false
  startedFromBody.value = false
  isOpen.value = true

  if (props.screenName) {
    Tracker.trackViewScreen(props.screenName)
  }

  emit('open')

  disableDocumentScroll()
  await nextTick()
  updateStickyFooterShadow()

  window.addEventListener('resize', updateStickyFooterShadow)
  document.addEventListener('keydown', handleKeydown)
}

const close = ({ resetDragOffset = true } = {}) => {
  if (!isOpen.value) return

  isOpen.value = false
  if (resetDragOffset) {
    dragOffsetY.value = 0
  }
  isDraggingDown.value = false
  startedFromBody.value = false

  window.removeEventListener('resize', updateStickyFooterShadow)
  document.removeEventListener('keydown', handleKeydown)
  restoreDocumentScroll()

  emit('close')
}

const handleTouchStart = (event) => {
  const [touch] = event.touches

  touchStartY.value = touch.clientY
  touchLastY.value = touch.clientY
  isDraggingDown.value = false
  dragOffsetY.value = 0
  startedFromBody.value = !!bodyRef.value?.contains(event.target)
}

const shouldIgnoreDragDown = () => {
  if (!startedFromBody.value || !bodyRef.value) {
    return false
  }

  return bodyRef.value.scrollTop > 0
}

const handleTouchMove = (event) => {
  const [touch] = event.touches
  const deltaY = touch.clientY - touchStartY.value

  touchLastY.value = touch.clientY

  if (deltaY <= 0) return
  if (shouldIgnoreDragDown()) return

  isDraggingDown.value = true
  dragOffsetY.value = deltaY
  event.preventDefault()
}

const handleTouchEnd = async () => {
  const totalDeltaY = touchLastY.value - touchStartY.value

  if (isDraggingDown.value && totalDeltaY > SWIPE_CLOSE_THRESHOLD) {
    // Leave dragging mode first so leave transition can animate down.
    isDraggingDown.value = false
    await nextTick()
    close({ resetDragOffset: false })
    return
  }

  dragOffsetY.value = 0
  isDraggingDown.value = false
  startedFromBody.value = false
}

const handleTouchCancel = () => {
  dragOffsetY.value = 0
  isDraggingDown.value = false
  startedFromBody.value = false
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStickyFooterShadow)
  document.removeEventListener('keydown', handleKeydown)

  if (isOpen.value) {
    restoreDocumentScroll()
  }
})

defineExpose({
  open,
  close,
})
</script>

<style lang="scss" scoped>
.bottom-sheet-mask {
  position: fixed;
  z-index: 9998;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) 0
    env(safe-area-inset-left, 0px);
  box-sizing: border-box;

  @include media-up(xl) {
    align-items: center;
  }

  &__container {
    --bottom-sheet-header-padding-x: #{$kds-space-2_5x};
    --bottom-sheet-content-padding-x: #{$kds-space-2_5x};
    --bottom-sheet-base-translate-y: 0%;
    --bottom-sheet-drag-offset: 0px;
    --bottom-sheet-scale: 1;
    --bottom-sheet-opacity: 1;
    background-color: #fff;
    width: 100%;
    max-width: 100%;
    max-height: calc(100dvh - 16px);
    border-radius: 16px 16px 0 0;
    box-shadow: 0 0 24px $kds-color-overlay-subtle;
    display: flex;
    flex-direction: column;
    will-change: height, transform;
    overflow: hidden;
    transform: translateY(var(--bottom-sheet-base-translate-y))
      translateY(var(--bottom-sheet-drag-offset))
      scale(var(--bottom-sheet-scale));
    opacity: var(--bottom-sheet-opacity);
    transition:
      transform 0.25s ease,
      opacity 0.25s ease;

    @include media-up(md) {
      width: calc($br-m - $kds-space-3x);
      max-width: calc(100vw - $kds-space-3x);
      --bottom-sheet-content-padding-x: #{$kds-space-6x};
    }

    @include media-up(xl) {
      max-height: calc(100dvh - $kds-space-4x);
      border-radius: 16px;
    }

    &--dragging {
      transition: none;
    }

    &--header {
      display: grid;
      grid-template-columns: 38px 1fr 38px;
      align-items: center;
      padding: $kds-space-2x var(--bottom-sheet-header-padding-x);
      gap: $kds-space-1x;

      &--close-button {
        width: 38px;
        height: 38px;
        border: 0;
        border-radius: 100%;
        padding: 0;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        :deep(svg path) {
          fill: $kds-color-icon-interactive-accent;
        }
      }

      &--title {
        margin: 0;
        text-align: center;
        @include kds-typography-title-large;
      }

      &--spacer {
        width: 38px;
        height: 38px;
      }
    }

    &--body {
      padding: 0 var(--bottom-sheet-content-padding-x)
        calc($kds-space-2x + env(safe-area-inset-bottom, 0px));
      min-height: 0;
      flex: 1 1 auto;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;

      &--has-footer {
        padding-bottom: $kds-space-2x;
      }
    }

    &--footer {
      padding: 0 var(--bottom-sheet-content-padding-x)
        calc($kds-space-2x + env(safe-area-inset-bottom, 0px));
      flex: 0 0 auto;
      background-color: $kds-color-background-base;

      &-show-shadow {
        box-shadow: 0 -1.5px 0 0 $kds-color-background-interactive-subtle-hover;
      }
    }
  }
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  background-color: transparent;
}

.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: background-color 0.25s ease;
}

.bottom-sheet-enter-from .bottom-sheet-mask__container,
.bottom-sheet-leave-to .bottom-sheet-mask__container {
  --bottom-sheet-base-translate-y: 100%;

  @include media-up(xl) {
    --bottom-sheet-base-translate-y: 0%;
    --bottom-sheet-scale: 0.97;
    --bottom-sheet-opacity: 0;
  }
}
</style>
